import Grid from '@mui/material/Grid';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { type JSX, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerTopBox } from '../shared/TargetingDrawer/DrawerTopBox';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { AdvancedTargetingSectionReadOnly } from './AdvancedTargetingSectionReadOnly';
import { ContextualSegmentsDrawerTop } from './ContextualSegmentsDrawerTop';
import { useContextualSegmentsDrawerState } from './hooks/useDrawerState';
import { useGetContextualSegmentsComponents } from './hooks/useGetContextualSegmentsComponents';

/**
 * Standalone Contextual Segments card used outside OA (V1) when the standalone contextual segments flag is on.
 * OA uses `ContextualSegmentsTargeting` (V3) instead; without the flag segments are a section of Custom Targeting.
 */
export const AdvancedContextualSegments = memo(function AdvancedContextualSegments({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  accountUid = '',
}: TargetingProps<CustomVariablesDataPayload[]>): JSX.Element {
  const { t } = useTranslation();
  const { ContextualSegmentsItems, segmentsV2, useAccountProviderId } = useGetContextualSegmentsComponents();
  const accountProviderId = useAccountProviderId(accountUid);
  const { initState, params, setParams, restore, onAdd, isUpdated, fieldValue } = useContextualSegmentsDrawerState(
    targetingParams,
    segmentsV2,
    accountProviderId
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdate(field, fieldValue),
    restoreParams: restore,
  });

  // The field can be cleared from outside (e.g. account change) while the drawer is closed.
  const onOpenDrawerClick = useCallback(() => {
    restore();
    onDrawerOpen();
  }, [restore, onDrawerOpen]);

  const onRemove = useCallback(() => onFieldUpdate(field, []), [field, onFieldUpdate]);

  return (
    <>
      <AdvancedTargetingSectionReadOnly
        name={name}
        field={field}
        sectionName={t('Contextual Segments')}
        description={t('Target users based on contextual segments from integrated data providers')}
        hasValue={!!targetingParams?.length}
        readonly={readonly}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemove}
      >
        <ContextualSegmentsItems readonly targetingParams={initState} accountUid={accountUid} />
      </AdvancedTargetingSectionReadOnly>

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isUpdated}>
          <DrawerTopBox>
            <Grid container sx={{ flexGrow: 1 }}>
              <ContextualSegmentsDrawerTop
                params={params}
                updateParams={setParams}
                onAdd={onAdd}
                accountUid={accountUid}
              />
            </Grid>
          </DrawerTopBox>

          <ContextualSegmentsItems
            targetingParams={params}
            readonly={false}
            onChange={setParams}
            onAdd={onAdd}
            accountUid={accountUid}
          />
        </TargetingDrawer>
      )}
    </>
  );
});
